import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';

/**
 * Custom tooltip for strength exercises
 */
const StrengthTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div style={{
        background: 'white',
        padding: '12px 16px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        border: '1px solid #e2e8f0'
      }}>
        <p style={{ margin: 0, fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>
          {data.dateLabel}
        </p>
        <p style={{ margin: '8px 0 0 0', color: '#667eea', fontWeight: 700, fontSize: '16px' }}>
          {data.maxWeight} kg
        </p>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
          ~{data.avgReps} reps × {data.totalSets} serie
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Custom tooltip for cardio exercises
 */
const CardioTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div style={{
        background: 'white',
        padding: '12px 16px',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        border: '1px solid #e2e8f0'
      }}>
        <p style={{ margin: 0, fontWeight: 600, color: '#1e293b', fontSize: '14px' }}>
          {data.dateLabel}
        </p>
        {data.distance > 0 && (
          <p style={{ margin: '8px 0 0 0', color: '#667eea', fontWeight: 700, fontSize: '16px' }}>
            {data.distance} km
          </p>
        )}
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
          {data.duration} min {data.avgSpeed > 0 ? `· ${data.avgSpeed} km/h` : ''}
        </p>
      </div>
    );
  }
  return null;
};

/**
 * ProgressChart - Grafico andamento peso/distanza
 *
 * @param {Array} data - Array di { dateLabel, maxWeight, avgReps, totalSets } per forza
 *                       oppure { dateLabel, distance, duration, avgSpeed } per cardio
 * @param {number} recordWeight - Peso record da mostrare come linea di riferimento (solo forza)
 * @param {boolean} isCardio - Se true, mostra il grafico cardio (distanza)
 */
function ProgressChart({ data, recordWeight, isCardio = false }) {
  if (!data || data.length === 0) {
    return (
      <div style={{
        height: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        borderRadius: '12px',
        color: '#64748b',
        fontSize: '14px'
      }}>
        Dati insufficienti per il grafico
      </div>
    );
  }

  if (isCardio) {
    // Cardio chart - show distance
    const distances = data.map(d => d.distance).filter(Boolean);
    if (distances.length === 0) {
      // If no distance data, show duration instead
      const durations = data.map(d => d.duration).filter(Boolean);
      const minDuration = Math.min(...durations);
      const maxDuration = Math.max(...durations);
      const padding = Math.max((maxDuration - minDuration) * 0.2, 5);

      return (
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <LineChart
              data={data}
              margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis
                dataKey="dateLabel"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickLine={false}
                axisLine={{ stroke: '#e2e8f0' }}
                interval="preserveStartEnd"
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
                domain={[Math.floor(minDuration - padding), Math.ceil(maxDuration + padding)]}
                tickFormatter={(value) => `${value}min`}
                width={50}
              />
              <Tooltip content={<CardioTooltip />} />
              <Line
                type="monotone"
                dataKey="duration"
                stroke="#f97316"
                strokeWidth={3}
                dot={{ fill: '#f97316', strokeWidth: 2, stroke: 'white', r: 4 }}
                activeDot={{ fill: '#f97316', strokeWidth: 2, stroke: 'white', r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    }

    const minDistance = Math.min(...distances);
    const maxDistance = Math.max(...distances);
    const padding = Math.max((maxDistance - minDistance) * 0.2, 0.5);

    return (
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <LineChart
            data={data}
            margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="dateLabel"
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              tickLine={false}
              axisLine={{ stroke: '#e2e8f0' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#94a3b8' }}
              tickLine={false}
              axisLine={false}
              domain={[Math.max(0, Math.floor(minDistance - padding)), Math.ceil(maxDistance + padding)]}
              tickFormatter={(value) => `${value}km`}
              width={45}
            />
            <Tooltip content={<CardioTooltip />} />
            <Line
              type="monotone"
              dataKey="distance"
              stroke="#f97316"
              strokeWidth={3}
              dot={{ fill: '#f97316', strokeWidth: 2, stroke: 'white', r: 4 }}
              activeDot={{ fill: '#f97316', strokeWidth: 2, stroke: 'white', r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Strength chart - show weight
  const weights = data.map(d => d.maxWeight).filter(Boolean);
  const minWeight = Math.min(...weights);
  const maxWeight = Math.max(...weights);
  const padding = Math.max((maxWeight - minWeight) * 0.2, 5);

  return (
    <div style={{ width: '100%', height: 220 }}>
      <ResponsiveContainer>
        <LineChart
          data={data}
          margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
        >
          <CartesianGrid
            strokeDasharray="3 3"
            stroke="#e2e8f0"
            vertical={false}
          />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={{ stroke: '#e2e8f0' }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#94a3b8' }}
            tickLine={false}
            axisLine={false}
            domain={[
              Math.floor(minWeight - padding),
              Math.ceil(maxWeight + padding)
            ]}
            tickFormatter={(value) => `${value}kg`}
            width={45}
          />
          <Tooltip content={<StrengthTooltip />} />

          {/* Reference line for record */}
          {recordWeight && (
            <ReferenceLine
              y={recordWeight}
              stroke="#10b981"
              strokeDasharray="5 5"
              strokeWidth={2}
              label={{
                value: 'Record',
                position: 'right',
                fill: '#10b981',
                fontSize: 11,
                fontWeight: 600
              }}
            />
          )}

          <Line
            type="monotone"
            dataKey="maxWeight"
            stroke="#667eea"
            strokeWidth={3}
            dot={{
              fill: '#667eea',
              strokeWidth: 2,
              stroke: 'white',
              r: 4
            }}
            activeDot={{
              fill: '#667eea',
              strokeWidth: 2,
              stroke: 'white',
              r: 6
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default ProgressChart;
